<template>
  <div class="field relative" v-show="!hidden" @click="focusInput">
    <VField :name="name" v-slot="{ field: { value, ...fieldProps }, meta, errors }">
      <input
        ref="inputRef"
        type="text"
        :value="value || modelValue"
        v-bind="fieldProps"
        class="base-textinput peer"
        @blur="$emit('blur', $event)"
        :class="{
          'placeholder-transparent': label,
          'bg-secondary/20': !meta.valid && meta.touched,
          'focus:placeholder-slate-400 px-3 placeholder': placeholder,
        }"
        :placeholder="label ? label : placeholder"
      />

      <label v-if="label" class="base-textinput-label">
        {{ label }}
      </label>
      <VErrorMessage :name="name" as="div" class="md:mt-1 mb-2 -mt-3 text-xs text-red-500 px-2" />
    </VField>
  </div>
</template>

<script setup lang="ts">
import { AddressFinder } from "@ideal-postcodes/address-finder";
import type { AnyAddress } from "@ideal-postcodes/jsutil";
import { isGbrAddress } from "@ideal-postcodes/jsutil";

const inputRef = ref<HTMLInputElement | null>(null);
const controller = ref<any>(null);

export type AutocompletePlace = {
  country: string;
  state: string;
  city: string;
  postal_code: string;
  address_line_1: string;
  address_line_2: string;
};

interface Props {
  name: string;
  countryCode: string;
  placeholder?: string;
  country?: string;
  label?: string;
  modelValue?: string;
  hidden?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: "",
  label: "",
  country: "",
  countryCode: "",
  modelValue: "",
  hidden: false,
});

const emit = defineEmits<{
  "update:placeSelected": [place: any];
  input: [event: Event];
  "update:modelValue": [value: string];
  blur: [];
}>();

const defaultAddressComponents = {
  country: "",
  state: "",
  city: "",
  postal_code: "",
  address_line_1: "",
  address_line_2: "",
};

const configureAddressFinder = () => {
  if (!inputRef.value) return;

  if (controller.value) {
    controller.value.detach();
  }

  controller.value = AddressFinder.setup({
    apiKey: useRuntimeConfig().public.apiIdealPost as string,
    inputField: inputRef.value,
    detectCountry: false,
    defaultCountry: props.countryCode,
    restrictCountries: [props.countryCode],

    onAddressRetrieved: (address: AnyAddress) => {
      const placeData = makeAddressObject(address);
      emit("update:placeSelected", placeData as AutocompletePlace);
    },

    onAddressSelected: (address) => {
      emit("update:modelValue", address?.suggestion as string);
    },
  });
};

const makeAddressObject = (address: AnyAddress): AutocompletePlace => {
  const addressComponents: AutocompletePlace = { ...defaultAddressComponents };

  // 🇬🇧 UK / GBR Address
  if (isGbrAddress(address)) {
    addressComponents.country = address.country ?? "";
    addressComponents.city = address.post_town ?? address.dependant_locality ?? "";
    addressComponents.state = address.county ?? address.administrative_county ?? "";
    addressComponents.postal_code = address.postcode ?? "";
    addressComponents.address_line_1 = address.line_1 ?? "";
    addressComponents.address_line_2 = address.line_2 ?? address.dependant_locality ?? "";
  } else {
    // Other countries (USA, Turkey, etc.)
    addressComponents.country = address.country ?? "";
    addressComponents.city = "city" in address ? address.city : "";
    addressComponents.state = "state" in address ? address.state : "";
    addressComponents.postal_code =
      ("zip_code" in address ? (address.zip_code as string) : "") ||
      ("postal_code" in address ? (address.postal_code as string) : "") ||
      ("postcode" in address ? (address.postcode as string) : "") ||
      "";

    let line_1 = "";
    if ("line_1" in address) {
      line_1 = address.line_1 as string;
    }
    if ("primary_number" in address && "street_name" in address) {
      line_1 = [address.primary_number ?? "", address.street_name ?? ""].filter(Boolean).join(" ").trim();
    }

    addressComponents.address_line_1 = line_1;
    addressComponents.address_line_2 = "line_2" in address ? (address.line_2 as string) : "";
  }

  return addressComponents;
};

const focusInput = () => {
  inputRef.value?.focus();
};

onMounted(() => {
  configureAddressFinder();
});

onUnmounted(() => {
  if (controller.value) {
    controller.value.detach();
  }
});

watch(
  () => props.countryCode,
  () => {
    configureAddressFinder();
  }
);
</script>
